function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React from "react";
import PropTypes from "prop-types";
import { connect, ReactReduxContext } from "react-redux";
import { get } from "lodash";
import * as Sentry from "@sentry/react";
import { compose } from "recompose";
import { TrackerProvider } from "@dpdgroupuk/react-event-tracker";
import FeedbackProvider from "@dpdgroupuk/react-sentry-feedback";
import { Banner, Loader, Snackbar, ThemeProvider } from "@dpdgroupuk/mydpd-ui";
import { EventType } from "@dpdgroupuk/fetch-client";
import { AppActions, AuthService, ErrorBoundary, UserPersistGate, AppVersion } from "./features";
import { createLogonApisClient } from "./apis";
import { EnvUtil, QueryUtil, CookieUtil } from "./utils";
import { FRIENDLY_PROJECT_NAME, ROUTE } from "./constants";
import { history, Router } from "./router";
import UrlResolver from "./UrlResolver";
import { AppContext } from "./Context";
import { ApisPropType, ProjectNamePropType, UrlParamsPropType } from "./PropTypes";

class Provider extends React.Component {
  constructor(props, context) {
    super(props, context);

    _defineProperty(this, "handleErrorListener", error => {
      if (!window.location.pathname.includes(ROUTE.LOGON_PAGE) && !window.location.pathname.includes(ROUTE.PLATFORMS)) {
        // attention: use withLoader from mydpd-app instead to properly handle 401 on load data
        if (error.statusCode === 401 || error.statusCode === 403 || error.statusCode === 429) {
          // don't log such error in sentry, just redirect user to login
          this.props.clearAppUserData();
          history.replace({
            pathname: ROUTE.LOGON_PAGE,
            search: QueryUtil.stringifyQuery({
              carrier: EnvUtil.getBusinessIdFromOrigin(window.location),
              returnUrl: window.location.pathname
            }, false)
          });
        }
      }
    });

    this.urlResolver = new UrlResolver(props.urlParams);
    this.auth = new AuthService(props.apis);
    this.logonApis = createLogonApisClient({
      urlResolver: this.urlResolver
    });
  }

  componentDidMount() {
    const searchParams = new URLSearchParams(window.location.search);

    if (searchParams.has("appPort")) {
      // MyDPD Customer app may run on different ports.
      CookieUtil.set("appPort", searchParams.get("appPort"));
    } // global place to handle promise rejection


    this.props.apis.on(EventType.Error, this.handleErrorListener);
  }

  componentWillUnmount() {
    this.props.apis.off(EventType.Error, this.handleErrorListener);
  }

  render() {
    return /*#__PURE__*/React.createElement(AppContext.Provider, {
      value: {
        apis: this.props.apis,
        auth: this.auth,
        logonApis: this.logonApis,
        state: this.props.state,
        urlResolver: this.urlResolver,
        projectName: this.props.projectName
      }
    }, /*#__PURE__*/React.createElement(ThemeProvider, {
      material: this.props.material,
      enableHelperTextTooltip: this.props.enableHelperTextTooltip,
      isLabelAnimated: this.props.isLabelAnimated
    }, /*#__PURE__*/React.createElement(Snackbar.Provider, null, /*#__PURE__*/React.createElement(Banner.Provider, null, /*#__PURE__*/React.createElement(FeedbackProvider, {
      hub: Sentry.getCurrentHub(),
      friendlyProjectName: FRIENDLY_PROJECT_NAME[this.props.projectName]
    }, /*#__PURE__*/React.createElement(TrackerProvider, {
      tracker: this.props.tracker
    }, /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(UserPersistGate, {
      store: this.context.store,
      userId: this.props.userId
    }, /*#__PURE__*/React.createElement(Loader, {
      loadFn: this.props.launchApp
    }, /*#__PURE__*/React.createElement(AppVersion, {
      store: this.context.store
    }, /*#__PURE__*/React.createElement(Router, {
      history: history
    }, this.props.children)))))))))));
  }

}

_defineProperty(Provider, "contextType", ReactReduxContext);

const ProviderPropTypes = {
  children: PropTypes.node,
  state: PropTypes.object,
  // private usage only
  apis: ApisPropType,
  launchApp: PropTypes.func,
  clearAppUserData: PropTypes.func,
  projectName: ProjectNamePropType,
  urlParams: UrlParamsPropType,
  tracker: PropTypes.object,
  userId: PropTypes.string,
  // Theme props
  material: PropTypes.bool,
  enableHelperTextTooltip: PropTypes.bool,
  isLabelAnimated: PropTypes.bool
};
Provider.propTypes = ProviderPropTypes;

const mapStateToProps = (state, ownProps) => ({
  state: state[ownProps.prefix],
  userId: get(state[ownProps.prefix], "auth.user.userId")
});

const mapDispatchToProps = (dispatch, {
  apis
}) => ({
  clearAppUserData: () => dispatch(AppActions.clearAppUserData()),
  launchApp: async () => {
    try {
      await dispatch(AppActions.getAppUser(apis));
    } catch (e) {
      // specific case, we just need to know here that user is not authorized
      if (e.statusCode === 401) {
        return; // do nothing
      }

      throw e;
    }
  }
});

const ReduxifyProvider = compose( // renameProp("apisClient", "apis"),
connect(mapStateToProps, mapDispatchToProps))(Provider);
ReduxifyProvider.defaultProps = {
  prefix: "app",
  urlParams: EnvUtil.resolveDefaultUrlParams(window.location)
};
export default ReduxifyProvider;